import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { aboutImages, stats as mockStats, leaders } from "../mock";
import { useI18n } from "../i18n/I18nContext";

const About = () => {
  const { t, localize } = useI18n();
  const stats = localize("stats", mockStats);
  const values = [
    { title: t("aboutV1t"), text: t("aboutV1x") },
    { title: t("aboutV2t"), text: t("aboutV2x") },
    { title: t("aboutV3t"), text: t("aboutV3x") },
  ];
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[440px] overflow-hidden bg-[#0a2540]">
        <img src={aboutImages.hero} alt="BPC" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a2540] via-[#0a2540]/50 to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-5 md:px-8 flex items-end pb-16">
          <div className="text-white max-w-3xl">
            <span className="text-sm font-700 uppercase tracking-widest text-[#5eb0f5]">
              {t("aboutBpc")}
            </span>
            <h1 className="font-display font-800 text-4xl md:text-6xl mt-3 leading-[1.05]">
              {t("aboutTitle")}
            </h1>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] leading-tight">
              {t("aboutStoryTitle")}
            </h2>
          </div>
          <div className="lg:col-span-7 text-slate-600 text-lg leading-relaxed space-y-5">
            <p>{t("aboutStory1")}</p>
            <p>{t("aboutStory2")}</p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-display font-800 text-4xl md:text-5xl text-[#0a6ed1]">{s.value}</div>
              <div className="mt-2 text-slate-500 text-[15px]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <span className="text-sm font-700 uppercase tracking-widest text-[#0a6ed1]">
          {t("ourValues")}
        </span>
        <h2 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] mt-2 mb-14">
          {t("whatWeStandFor")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {values.map((v) => (
            <div key={v.title} className="border-t-2 border-[#0a6ed1] pt-6">
              <h3 className="font-display font-700 text-xl text-[#0a2540]">{v.title}</h3>
              <p className="mt-3 text-slate-500 text-[15px] leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <h2 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] mb-14">
            {t("leadership")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {leaders.map((l) => (
              <div key={l.id} className="hover-zoom group">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-slate-200">
                  <img src={l.image} alt={l.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="mt-4 font-display font-700 text-lg text-[#0a2540]">{l.name}</h3>
                <p className="text-slate-500 text-sm">{l.role}</p>
              </div>
            ))}
            <div className="flex flex-col justify-center bg-[#0a2540] rounded-lg p-8 text-white">
              <h3 className="font-display font-700 text-xl">{t("joinOurTeam")}</h3>
              <p className="mt-2 text-slate-300 text-sm">
                {t("joinTeamBody")}
              </p>
              <Link to="/contact" className="mt-4 inline-flex items-center gap-2 text-[#5eb0f5] font-600">
                {t("seeOpenRoles")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
